import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, AtSign, CheckCircle2, ExternalLink, FileText, Gauge, Globe, Info, KeyRound, Mail, Network, Plus, Power, RefreshCw, Settings, Trash2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { MailAliasRecord, MailboxRecord, MailDomainDetail, MailDomainRecord } from "@hostdock/types";
import { Button, cn, ConfirmDialog, CopyButton, Dialog, ErrorState, Field, FormError, formatRelative, Input, Select, Skeleton, Textarea, toast } from "@hostdock/ui";
import { api } from "../api";
import {
  actionsCell, Banner, Cell, EmptyCard, InfoCard, InfoRow, PageHero, Pill, primaryButtonClass, rowPrimaryClass, rowSecondaryClass, SectionCard, secondaryButtonClass, StatCard, tableCell,
  TableShell,
} from "../components/page-kit";
import { DOCS_HREF } from "../layout";

const externalDocs = /^https?:\/\//.test(DOCS_HREF);
const sizeLabel = (mb: number) => (mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 ? 1 : 0)} GB` : `${mb} MB`);

function DomainPill({ d }: { d: Pick<MailDomainRecord, "syncedAt" | "status"> }) {
  if (d.status === "SUSPENDED") return <Pill tone="wait" dot>Suspended</Pill>;
  return d.syncedAt ? <Pill tone="ok" dot>Active</Pill> : <Pill tone="wait" dot>Waiting for the server</Pill>;
}

// ---------------------------------------------------------------- overview

export function EmailPage() {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["mail"], queryFn: () => api.mail.overview(), refetchInterval: 20_000 });
  const m = q.data;
  const aliases = m?.domains.reduce((a, d) => a + d.aliases, 0) ?? 0;
  const cols = "xl:grid xl:grid-cols-[260px_200px_110px_110px_minmax(0,1fr)_116px] xl:items-center";

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Mail}
        title="Email"
        description="Mailboxes and aliases for the domains on your websites."
        action={<button type="button" disabled={!m?.available} onClick={() => setAdding(true)} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Add email domain</button>}
      />
      {m && m.limit === 0 && <Banner tone="info" icon={Info} title="Email is not part of your plan">Your current plan does not include mailboxes. Contact support to add email hosting.</Banner>}
      {m && m.limit > 0 && !m.available && <Banner icon={TriangleAlert} title="Email is not available on your server yet">Email hosting has not been enabled on the server that runs your websites. Contact support to have it set up.</Banner>}

      {m && m.limit > 0 && (
        <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={Globe} tile="teal" label="Email domains" value={m.domains.length} />
          <StatCard icon={Mail} tile="blue" label="Mailboxes used" value={`${m.used} / ${m.limit}`} sub={m.used >= m.limit ? "Plan allowance reached" : `${m.limit - m.used} more available`} />
          <StatCard icon={AtSign} tile="green" label="Aliases" value={aliases} sub="Forwarding addresses" />
          <StatCard icon={Gauge} tile="orange" label="Mailbox size" value={sizeLabel(m.quotaMb)} sub="Storage per mailbox" />
        </div>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !m || m.domains.length === 0 ? (
          <EmptyCard icon={Mail} title="No email domains yet" body={m?.available ? "Add a domain to create mailboxes such as info@yourdomain.com." : "Email becomes available once it is part of your plan and enabled on your server."} />
        ) : (
          <TableShell label="Email domains" cols={cols} headers={["Domain", "Status", "Mailboxes", "Aliases", "Signing key", "Actions"]}>
            {m.domains.map((d) => (
              <li key={d.id} role="row" aria-label={d.domain} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[#e6f0fc] text-[#2f6fd6] dark:bg-surface-3 dark:text-info"><Mail size={21} strokeWidth={1.7} /></span>
                  <div className="min-w-0 leading-tight">
                    <Link to={`/email/${d.id}`} className="block truncate text-[14px] font-bold hover:text-accent">{d.domain}</Link>
                    <span className="mt-[4px] block text-[11.5px] text-muted">Added {formatRelative(d.createdAt)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                  <Cell label="Status" className={tableCell}><DomainPill d={d} /></Cell>
                  <Cell label="Mailboxes" className={tableCell}><span className="tabular-nums">{d.mailboxes}</span></Cell>
                  <Cell label="Aliases" className={tableCell}><span className="tabular-nums">{d.aliases}</span></Cell>
                  <Cell label="Signing key" className={tableCell}><span className="max-w-full truncate font-mono text-[12px] text-muted">DKIM · {d.dkimSelector}</span></Cell>
                </div>
                <div role="cell" className={actionsCell}>
                  <button type="button" onClick={() => navigate(`/email/${d.id}`)} className={cn(rowPrimaryClass, "xl:w-[100px]")}><Settings size={15} className="shrink-0" />Manage</button>
                </div>
              </li>
            ))}
          </TableShell>
        )}
      </div>

      <InfoRow>
        <InfoCard to="/dns" icon={Network} tile="bg-[#d5f4f6] text-[#0a8ea0]" className="bg-[#eef9fb] dark:bg-surface-2" title="Publish your mail DNS records"
          body="Each email domain lists its MX, SPF, DKIM and DMARC records. Add them where your DNS is managed (or in DNS here, if it's connected) so mail is delivered and trusted." />
        <InfoCard to={DOCS_HREF} external={externalDocs} icon={FileText} tile="bg-[#d9eafe] text-[#2f6fd6]" className="bg-[#eff5fc] dark:bg-surface-2" title="Need help with email?" body="Learn how to set up mailboxes, aliases and forwarding, use webmail, and connect a mail app on your phone or computer." />
      </InfoRow>

      <AddDomainDialog open={adding} onOpenChange={setAdding} existing={m?.domains.map((d) => d.domain) ?? []} onCreated={(d) => navigate(`/email/${d.id}`)} />
    </div>
  );
}

function AddDomainDialog({ open, onOpenChange, existing, onCreated }: { open: boolean; onOpenChange(o: boolean): void; existing: string[]; onCreated(d: MailDomainDetail): void }) {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const domains = useQuery({ queryKey: ["domains", "mail"], queryFn: () => api.domains.list({ pageSize: 100 }), enabled: open });
  const choices = (domains.data?.items ?? []).filter((d) => d.status === "VERIFIED" && !existing.includes(d.name));
  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.mail.createDomain({ domain }),
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ["mail"] }); toast.success("Email domain added"); onOpenChange(false); setDomain(""); onCreated(d); },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add an email domain" description="Choose one of your verified website domains."
      footer={<><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!domain} loading={create.isPending} onClick={() => create.mutate()}>Add domain</Button></>}>
      <Field label="Domain" hint="Only domains whose DNS already points to HostDock can be used, so nobody can set up mail for a domain they do not control.">
        {(p) => <Select {...p} value={domain} onChange={(e) => setDomain(e.target.value)}><option value="">Choose a domain…</option>{choices.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</Select>}
      </Field>
      {domains.data && choices.length === 0 && <p className="mt-3 rounded-lg border border-[#f9dcc0] bg-[#fff8f0] p-3 text-[12.5px] text-muted dark:border-warning/30 dark:bg-warning-soft">No verified domains are available. Add and verify a domain on one of your websites first.</p>}
      <FormError error={create.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------- domain

export function EmailDomainPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "domain", id], queryFn: () => api.mail.domain(id), refetchInterval: 15_000 });
  const mailboxes = useQuery({ queryKey: ["mail", "mailboxes", id], queryFn: () => api.mail.mailboxes(id) });
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["mail"] }); };
  const sync = useMutation({ meta: { inlineError: true }, mutationFn: () => api.mail.sync(id), onSuccess: () => { toast.success("Applied on the server"); refresh(); } });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (domain: string) => api.mail.deleteDomain(id, domain), onSuccess: () => { toast.success("Email domain removed"); refresh(); navigate("/email"); } });
  if (q.isPending) return <div className="lg:px-[13px]"><Skeleton className="h-40 w-full rounded-xl" /></div>;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const boxes = mailboxes.data?.items;
  const active = boxes?.filter((b) => b.status === "ACTIVE").length;
  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Mail}
        title={d.domain}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/email" className="inline-flex items-center gap-1 font-medium text-accent hover:underline"><ArrowLeft size={13} />All email</Link>
            <DomainPill d={d} />
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setDeleting(true)} className={cn(secondaryButtonClass, "text-danger")}><Trash2 size={15} />Remove domain</button>
            <button type="button" onClick={() => setCreating(true)} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />New mailbox</button>
          </div>
        }
      />
      {!d.syncedAt && (
        <Banner icon={RefreshCw} title="Changes are waiting for the server"
          action={<button type="button" onClick={() => sync.mutate()} disabled={sync.isPending} className={secondaryButtonClass}><RefreshCw size={15} className={sync.isPending ? "animate-spin" : undefined} />Apply now</button>}>
          The latest changes are saved but the mail server has not confirmed them yet.
          <FormError error={sync.error} />
        </Banner>
      )}

      <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
        <StatCard icon={Mail} tile="blue" label="Mailboxes" value={boxes ? boxes.length : d.mailboxes} />
        <StatCard icon={CheckCircle2} tile="green" label="Active mailboxes" value={active ?? "…"} sub={boxes && boxes.length - (active ?? 0) ? `${boxes.length - (active ?? 0)} disabled` : "All can sign in"} />
        <StatCard icon={AtSign} tile="teal" label="Aliases" value={d.aliases} sub="Forwarding addresses" />
        <StatCard icon={KeyRound} tile="orange" label="Signing key" value={d.dkimSelector} sub="DKIM selector" />
      </div>

      <Mailboxes domain={d} q={mailboxes} onChange={refresh} creating={creating} setCreating={setCreating} />
      <Aliases domain={d} onChange={refresh} />
      <DnsRecords domain={d} />

      <ConfirmDialog
        open={deleting} onOpenChange={setDeleting} title={`Remove email for ${d.domain}?`} confirmText={d.domain} confirmLabel="Remove domain"
        description="Mailboxes and aliases for this domain stop working and its signing key is deleted. Messages already stored on the server are not erased by this action."
        loading={remove.isPending} error={remove.error} onConfirm={() => remove.mutate(d.domain)}
      />
    </div>
  );
}

const PURPOSE_PILL: Record<string, string> = {
  MX: "bg-[#e6f0fc] text-[#2f6fd6] dark:bg-info-soft dark:text-info",
  SPF: "bg-[#dff3f6] text-[#0a7f90] dark:bg-accent-soft dark:text-accent",
  DKIM: "bg-[#e2f8ee] text-[#14a04a] dark:bg-success-soft dark:text-success",
  DMARC: "bg-[#fdf0e3] text-[#c85a16] dark:bg-warning-soft dark:text-warning",
};

function DnsRecords({ domain }: { domain: MailDomainDetail }) {
  return (
    <SectionCard title="DNS records" description="Add these at the company that manages your domain's DNS. HostDock cannot change them for you, and does not check that they are published.">
      <ul className="grid gap-[11px] py-[14px] lg:grid-cols-2">
        {domain.dns.map((r) => (
          <li key={`${r.purpose}-${r.name}`} className="min-w-0 rounded-xl border border-line p-[14px]">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn("inline-flex h-[24px] items-center rounded-md px-2 text-[12px] font-semibold", PURPOSE_PILL[r.purpose])}>{r.purpose}</span>
              <span className="text-[12px] text-muted">{r.type} record{r.required ? "" : " (recommended)"}</span>
            </div>
            <dl className="mt-3 grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 gap-y-2 text-[12.5px]">
              <dt className="pt-1 text-subtle">Name</dt>
              <dd className="flex min-w-0 items-start justify-between gap-2"><span className="pt-1 font-mono break-all">{r.name}</span><CopyButton value={r.name} /></dd>
              {r.priority !== undefined && <><dt className="text-subtle">Priority</dt><dd className="font-mono">{r.priority}</dd></>}
              <dt className="pt-1 text-subtle">Value</dt>
              <dd className="flex min-w-0 items-start justify-between gap-2"><span className="pt-1 font-mono break-all">{r.value}</span><CopyButton value={r.value} /></dd>
            </dl>
            <p className="mt-3 text-[12px] leading-[17px] text-muted">{r.note}</p>
          </li>
        ))}
      </ul>
      {!domain.mailHost && <p className="mb-3 rounded-lg border border-[#f9dcc0] bg-[#fff8f0] p-3 text-[12.5px] text-muted dark:border-warning/30 dark:bg-warning-soft">The server has no public DNS name, so no mail (MX) record can be given yet. Contact support.</p>}
      <p className="pb-[14px] text-[12px] text-muted">Receivers also check reverse DNS for the server's IP address. That is set by the server provider, not by HostDock; ask support if mail to large providers is rejected.</p>
    </SectionCard>
  );
}

// ---------------------------------------------------------------- mailboxes

type MailboxQuery = { data?: { items: MailboxRecord[] }; isPending: boolean; error: unknown; refetch(): unknown };

function Mailboxes({ domain, q, onChange, creating, setCreating }: { domain: MailDomainDetail; q: MailboxQuery; onChange(): void; creating: boolean; setCreating(v: boolean): void }) {
  const [pw, setPw] = useState<MailboxRecord | null>(null);
  const [del, setDel] = useState<MailboxRecord | null>(null);
  const refresh = () => { void q.refetch(); onChange(); };
  const toggle = useMutation({ mutationFn: (m: MailboxRecord) => api.mail.updateMailbox(m.id, { status: m.status === "ACTIVE" ? "DISABLED" : "ACTIVE" }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (m: MailboxRecord) => api.mail.deleteMailbox(m.id), onSuccess: () => { toast.success("Mailbox removed"); setDel(null); refresh(); } });
  const webmail = async (m: MailboxRecord) => {
    const tab = window.open("about:blank", "_blank");
    try {
      const { url } = await api.mail.webmail(m.id);
      if (tab) { tab.opener = null; tab.location.href = url; } else window.location.href = url;
    } catch (err) {
      tab?.close();
      toast.error(err instanceof Error ? err.message : "Could not open webmail");
    }
  };
  const items = q.data?.items;
  const cols = "xl:grid xl:grid-cols-[minmax(0,1fr)_104px_90px_130px_370px] xl:items-center";
  return (
    <SectionCard title="Mailboxes" count={items?.length} description="Each mailbox has its own password and can be used in webmail or any mail app.">
      {q.error ? <div className="py-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        : q.isPending ? <Skeleton className="my-4 h-24 w-full rounded-xl" />
          : !items?.length ? <p className="py-8 text-center text-[12.5px] text-muted"><span className="block text-[13.5px] font-semibold text-fg">No mailboxes yet</span>Create one with New mailbox, for example info@{domain.domain}.</p>
            : (
              <div role="table" aria-label="Mailboxes">
                <div className={cn(cols, "hidden h-[40px] border-b border-line text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase")} role="row">
                  {["Address", "Status", "Size limit", "Password changed", "Actions"].map((h, i) => <span key={h} role="columnheader" className={i ? "pl-[17px]" : undefined}>{h}</span>)}
                </div>
                <ul className="divide-y divide-line" role="rowgroup">
                  {items.map((m) => (
                    <li key={m.id} role="row" aria-label={m.address} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] xl:min-h-[62px] xl:gap-0 xl:py-[7px]")}>
                      <div role="cell" className="flex min-w-0 items-center gap-[12px] xl:pr-3">
                        <span className="truncate text-[13.5px] font-bold" title={m.address}>{m.address}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 xl:contents">
                        <Cell label="Status" className={tableCell}>{m.status === "ACTIVE" ? <Pill tone="ok" dot>Active</Pill> : <Pill tone="off" dot>Disabled</Pill>}</Cell>
                        <Cell label="Size limit" className={tableCell}><span className="text-muted">{sizeLabel(m.quotaMb)}</span></Cell>
                        <Cell label="Password changed" className={tableCell}><span className="text-muted">{m.passwordChangedAt ? formatRelative(m.passwordChangedAt) : "—"}</span></Cell>
                      </div>
                      <div role="cell" className={cn(actionsCell, "flex-wrap xl:flex-nowrap")}>
                        <button type="button" disabled={m.status !== "ACTIVE"} onClick={() => void webmail(m)} className={cn(rowPrimaryClass, "disabled:cursor-not-allowed disabled:opacity-50 xl:w-[122px]")}><ExternalLink size={14} className="shrink-0" />Open webmail</button>
                        <button type="button" onClick={() => setPw(m)} className={cn(rowSecondaryClass, "xl:w-[90px]")}><KeyRound size={14} className="shrink-0" />Password</button>
                        <button type="button" disabled={toggle.isPending && toggle.variables?.id === m.id} onClick={() => toggle.mutate(m)} className={cn(rowSecondaryClass, "disabled:opacity-60 xl:w-[78px]")}><Power size={14} className="shrink-0" />{m.status === "ACTIVE" ? "Disable" : "Enable"}</button>
                        <button type="button" onClick={() => setDel(m)} aria-label={`Delete ${m.address}`} title="Delete mailbox" className={cn(rowSecondaryClass, "flex-none px-[10px] text-danger hover:bg-danger-soft xl:w-[40px]")}><Trash2 size={15} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
      <MailboxDialog open={creating} onOpenChange={setCreating} domain={domain} onDone={refresh} />
      <PasswordDialog mailbox={pw} onClose={() => setPw(null)} onDone={refresh} />
      <ConfirmDialog
        open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Delete ${del?.address}?`} confirmText={del?.address} confirmLabel="Delete mailbox"
        description="The mailbox stops receiving mail and cannot be signed in to. Mail already stored on the server is not erased by this action."
        loading={remove.isPending} error={remove.error} onConfirm={() => del && remove.mutate(del)}
      />
    </SectionCard>
  );
}

function MailboxDialog({ open, onOpenChange, domain, onDone }: { open: boolean; onOpenChange(o: boolean): void; domain: MailDomainDetail; onDone(): void }) {
  const [local, setLocal] = useState("");
  const [password, setPassword] = useState("");
  const create = useMutation({
    mutationFn: () => api.mail.createMailbox(domain.id, { localPart: local, password }),
    onSuccess: (m) => { toast.success(`${m.address} created`); setLocal(""); setPassword(""); onOpenChange(false); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New mailbox"
      footer={<><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!local || password.length < 10} loading={create.isPending} onClick={() => create.mutate()}>Create mailbox</Button></>}>
      <div className="space-y-3">
        <Field label="Address">{(p) => <div className="flex items-center gap-1"><Input {...p} value={local} onChange={(e) => setLocal(e.target.value.toLowerCase())} placeholder="info" autoComplete="off" /><span className="text-[13px] text-muted">@{domain.domain}</span></div>}</Field>
        <Field label="Password" hint="At least 10 characters. HostDock keeps only a one-way hash and cannot show it again.">{(p) => <Input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <FormError error={create.error} />
      </div>
    </Dialog>
  );
}

function PasswordDialog({ mailbox, onClose, onDone }: { mailbox: MailboxRecord | null; onClose(): void; onDone(): void }) {
  const [password, setPassword] = useState("");
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.mail.updateMailbox(mailbox!.id, { password }),
    onSuccess: () => { toast.success("Password changed"); setPassword(""); onClose(); onDone(); },
  });
  return (
    <Dialog open={!!mailbox} onOpenChange={(o) => !o && onClose()} title={`Change password: ${mailbox?.address ?? ""}`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={password.length < 10} loading={save.isPending} onClick={() => save.mutate()}>Change password</Button></>}>
      <Field label="New password" hint="At least 10 characters. Mail programs using the old password will need the new one.">{(p) => <Input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
      <FormError error={save.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------- aliases

function Aliases({ domain, onChange }: { domain: MailDomainDetail; onChange(): void }) {
  const q = useQuery({ queryKey: ["mail", "aliases", domain.id], queryFn: () => api.mail.aliases(domain.id) });
  const [editing, setEditing] = useState(false);
  const refresh = () => { void q.refetch(); onChange(); };
  const remove = useMutation({ mutationFn: (a: MailAliasRecord) => api.mail.deleteAlias(a.id), onSuccess: () => { toast.success("Alias removed"); refresh(); } });
  const items = q.data?.items;
  const cols = "xl:grid xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_76px] xl:items-center";
  return (
    <SectionCard
      title="Aliases and forwarding"
      count={items?.length}
      description="Send mail for one address to other addresses. Use * to catch every address that has no mailbox."
      action={<button type="button" onClick={() => setEditing(true)} className={secondaryButtonClass}><Plus size={16} />New alias</button>}
    >
      {q.error ? <div className="py-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        : q.isPending ? <Skeleton className="my-4 h-16 w-full rounded-xl" />
          : !items?.length ? <p className="py-8 text-center text-[12.5px] text-muted"><span className="block text-[13.5px] font-semibold text-fg">No aliases</span>Forward an address such as sales@ to one or more mailboxes.</p>
            : (
              <div role="table" aria-label="Aliases">
                <div className={cn(cols, "hidden h-[40px] border-b border-line text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase")} role="row">
                  {["From", "Delivered to", ""].map((h, i) => <span key={h || i} role="columnheader" className={i ? "pl-[17px]" : undefined}>{h}</span>)}
                </div>
                <ul className="divide-y divide-line" role="rowgroup">
                  {items.map((a) => (
                    <li key={a.id} role="row" aria-label={`${a.source}@${domain.domain}`} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] xl:min-h-[56px] xl:gap-0 xl:py-[4px]")}>
                      <div role="cell" className="flex min-w-0 items-center xl:pr-3"><span className="truncate text-[13.5px] font-bold">{a.source === "*" ? "Everything else" : a.source}@{domain.domain}</span></div>
                      <Cell label="Delivered to" className={tableCell}><span className="max-w-full truncate text-muted" title={a.destinations.join(", ")}>{a.destinations.join(", ")}</span></Cell>
                      <div role="cell" className={actionsCell}>
                        <button type="button" disabled={remove.isPending && remove.variables?.id === a.id} onClick={() => remove.mutate(a)} aria-label={`Delete alias ${a.source}`} title="Delete alias" className={cn(rowSecondaryClass, "flex-none px-[10px] text-danger hover:bg-danger-soft disabled:opacity-60 xl:w-[40px]")}><Trash2 size={15} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
      <AliasDialog open={editing} onOpenChange={setEditing} domain={domain} onDone={refresh} />
    </SectionCard>
  );
}

function AliasDialog({ open, onOpenChange, domain, onDone }: { open: boolean; onOpenChange(o: boolean): void; domain: MailDomainDetail; onDone(): void }) {
  const [source, setSource] = useState("");
  const [dests, setDests] = useState("");
  const list = dests.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
  const save = useMutation({
    mutationFn: () => api.mail.saveAlias(domain.id, { source: source.toLowerCase(), destinations: list, enabled: true }),
    onSuccess: () => { toast.success("Alias saved"); setSource(""); setDests(""); onOpenChange(false); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New alias"
      footer={<><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!source || list.length === 0} loading={save.isPending} onClick={() => save.mutate()}>Save alias</Button></>}>
      <div className="space-y-3">
        <Field label="From" hint="Use * to catch all other addresses.">{(p) => <div className="flex items-center gap-1"><Input {...p} value={source} onChange={(e) => setSource(e.target.value)} placeholder="sales" autoComplete="off" /><span className="text-[13px] text-muted">@{domain.domain}</span></div>}</Field>
        <Field label="Deliver to" hint="One or more addresses (up to 10), separated by spaces or commas. Forwarding to other providers works only if they accept mail from this server.">{(p) => <Textarea {...p} rows={3} value={dests} onChange={(e) => setDests(e.target.value)} placeholder="info@example.com" />}</Field>
        <FormError error={save.error} />
      </div>
    </Dialog>
  );
}
